import { backendClient } from '../lib/api/backendClient';

export interface FavoriteArtist {
  id: string;
  spotifyArtistId: string;
  savedAt: string;
  name?: string;
  imageUrl?: string;
  uri?: string;
  genres?: string[];
  notes?: string;
}

const toIsoString = (value?: string | Date): string | undefined => {
  if (!value) return undefined;
  if (value instanceof Date) return value.toISOString();
  const parsed = new Date(value);
  return Number.isNaN(parsed.valueOf()) ? undefined : parsed.toISOString();
};

const deserializeFavoriteArtist = (payload: any): FavoriteArtist => {
  const stringOrUndefined = (value: unknown): string | undefined =>
    typeof value === 'string' && value.length ? value : undefined;

  const savedAt = typeof payload?.savedAt === 'string' ? payload.savedAt : undefined;
  const fallback =
    (typeof payload?.updated_at === 'string' && payload.updated_at) ||
    (typeof payload?.created_at === 'string' && payload.created_at) ||
    new Date().toISOString();

  const spotifyArtistId =
    stringOrUndefined(payload?.spotifyArtistId) ??
    stringOrUndefined(payload?.spotify_artist_id) ??
    stringOrUndefined(payload?.id) ??
    '';

  const rawGenres = payload?.genres;
  const genres =
    Array.isArray(rawGenres) && rawGenres.length
      ? rawGenres
          .map((entry: unknown) => (typeof entry === 'string' ? entry.trim() : ''))
          .filter((entry: string): entry is string => entry.length > 0)
      : undefined;

  return {
    id: stringOrUndefined(payload?.id) ?? spotifyArtistId,
    spotifyArtistId,
    notes: typeof payload?.notes === 'string' ? payload.notes : undefined,
    savedAt: toIsoString(savedAt) ?? fallback,
    name: stringOrUndefined(payload?.name),
    imageUrl: stringOrUndefined(payload?.imageUrl) ?? stringOrUndefined(payload?.image_url),
    uri: stringOrUndefined(payload?.uri),
    genres,
  };
};

export const favoriteArtistService = {
  async getFavoriteArtists(): Promise<FavoriteArtist[]> {
    const { data } = await backendClient.get<{ artists?: any[] }>('/favorites/artists');
    const artists = Array.isArray(data?.artists) ? data?.artists : [];
    return artists.map(deserializeFavoriteArtist);
  },

  async getFavoriteArtist(spotifyArtistId: string): Promise<FavoriteArtist | null> {
    if (!spotifyArtistId) return null;
    try {
      const { data } = await backendClient.get<{ artist?: any }>(
        `/favorites/artists/${encodeURIComponent(spotifyArtistId)}`
      );
      if (!data?.artist) return null;
      return deserializeFavoriteArtist(data.artist);
    } catch (error: any) {
      if (error?.response?.status === 404) return null;
      throw error;
    }
  },

  async saveFavoriteArtist(spotifyArtistId: string, options?: { notes?: string; savedAt?: string | Date }) {
    const payload: Record<string, unknown> = { spotifyArtistId };
    if (options?.notes) payload.notes = options.notes;
    if (options?.savedAt) payload.savedAt = toIsoString(options.savedAt);

    const { data } = await backendClient.post<{ artist: any }>('/favorites/artists', payload);
    return deserializeFavoriteArtist(data.artist);
  },

  async updateFavoriteArtist(spotifyArtistId: string, options?: { notes?: string; savedAt?: string | Date }) {
    const payload: Record<string, unknown> = {};
    if (options?.notes) payload.notes = options.notes;
    if (options?.savedAt) payload.savedAt = toIsoString(options.savedAt);

    const { data } = await backendClient.put<{ artist: any }>(
      `/favorites/artists/${encodeURIComponent(spotifyArtistId)}`,
      payload
    );
    return deserializeFavoriteArtist(data.artist);
  },

  async removeFavoriteArtist(spotifyArtistId: string): Promise<void> {
    await backendClient.delete(`/favorites/artists/${encodeURIComponent(spotifyArtistId)}`);
  },
};
